import { useState, useCallback } from 'react';
import { trpc } from '../lib/trpc.js';

type Source = 'meta' | 'zenbooker';

export function UploadPage() {
  const [source, setSource] = useState<Source>('meta');
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<{ sampleRows: unknown[]; totalRows: number; eurToGbpRate: number; errors: string[] } | null>(null);
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<{ created: number; updated: number; errors: string[]; totalRows: number } | null>(null);

  const parseMeta = trpc.upload.parseMetaPreview.useMutation({ onSuccess: (data) => setPreview(data) });
  const parseZb = trpc.upload.parseZenbookerPreview.useMutation({ onSuccess: (data) => setPreview(data) });
  const importMeta = trpc.upload.importMetaCsv.useMutation({
    onSuccess: (data) => { setResult(data); setPreview(null); setCsvContent(''); },
  });
  const importZb = trpc.upload.importZenbookerCsv.useMutation({
    onSuccess: (data) => { setResult(data); setPreview(null); setCsvContent(''); },
  });
  const history = trpc.upload.history.useQuery();

  const parseMutation = source === 'meta' ? parseMeta : parseZb;
  const importMutation = source === 'meta' ? importMeta : importZb;

  const handleFile = useCallback(async (file: File) => {
    const text = await file.text();
    setCsvContent(text);
    setFileName(file.name);
    setResult(null);
    parseMutation.mutate({ csvContent: text });
  }, [parseMutation]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.name.endsWith('.csv')) handleFile(file);
  }, [handleFile]);

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const switchSource = (s: Source) => {
    setSource(s);
    setPreview(null);
    setResult(null);
    setCsvContent('');
    setFileName('');
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="font-display text-3xl text-navy">Upload CSV</h1>

      <div className="flex gap-2">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium border ${source === 'meta' ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200'}`}
          onClick={() => switchSource('meta')}
        >
          Meta Ads
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium border ${source === 'zenbooker' ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200'}`}
          onClick={() => switchSource('zenbooker')}
        >
          Zenbooker bookings
        </button>
      </div>

      <div className="card">
        <h2 className="font-display text-xl text-navy mb-3">How to export</h2>
        {source === 'meta' ? (
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Open Meta Ads Manager → Ads view, select date range</li>
            <li>For best resolution: Breakdown → By Day (one row per ad-day)</li>
            <li>If exported as a date range (no By Day), totals are split evenly across the period</li>
            <li>Columns auto-detected: Ad name, Ad set name, Reporting starts/ends or Day, Amount spent (EUR), Results, CPM, CTR, Link clicks</li>
            <li>Export → CSV</li>
          </ol>
        ) : (
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Open Zenbooker → Schedule (or Bookings)</li>
            <li>Select date range (any length — re-imports overwrite by booking ID)</li>
            <li>Export → CSV</li>
            <li>Importer maps: <code className="text-xs">_id</code> → booking ID, <code className="text-xs">live_status_text</code> + <code className="text-xs">cancel_boolean</code> → status, <code className="text-xs">price_number</code> → job value, customer email/phone matched against existing leads when possible</li>
          </ol>
        )}
      </div>

      <div
        className={`card border-2 border-dashed transition-colors ${dragOver ? 'border-gold bg-gold/5' : 'border-gray-200'} text-center cursor-pointer`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => document.getElementById('csv-input')?.click()}
      >
        <input id="csv-input" type="file" accept=".csv" className="hidden" onChange={onFileInput} />
        <div className="py-8">
          <p className="text-gray-400 text-sm">Drop CSV here or click to browse</p>
          {fileName && <p className="mt-2 text-navy font-medium text-sm">{fileName}</p>}
        </div>
      </div>

      {parseMutation.isPending && <p className="text-sm text-gray-500">Parsing…</p>}

      {preview && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl text-navy">Preview</h2>
              <p className="text-sm text-gray-500">
                {preview.totalRows} rows
                {source === 'meta' && preview.eurToGbpRate > 0 ? ` · EUR→GBP rate: ${preview.eurToGbpRate.toFixed(4)}` : ''}
              </p>
            </div>
            <button
              className="btn-gold"
              onClick={() => importMutation.mutate({ csvContent, fileName })}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Importing…' : 'Confirm Import'}
            </button>
          </div>

          {preview.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <p className="text-xs font-medium text-red-700 mb-1">Errors in first 5 rows:</p>
              {preview.errors.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
            </div>
          )}

          <p className="text-xs text-gray-400 mb-2">First 5 rows (preview):</p>
          <div className="overflow-x-auto">
            <pre className="text-xs bg-cream-tint p-3 rounded font-mono">{JSON.stringify(preview.sampleRows, null, 2)}</pre>
          </div>
        </div>
      )}

      {result && (
        <div className={`card border ${result.errors.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <h2 className="font-display text-xl mb-2">{result.errors.length > 0 ? 'Import completed with warnings' : 'Import successful'}</h2>
          <p className="text-sm">
            <span className="font-mono font-medium">{result.created}</span> created ·{' '}
            <span className="font-mono font-medium">{result.updated}</span> updated ·{' '}
            <span className="font-mono font-medium">{result.errors.length}</span> errors
          </p>
          {result.errors.length > 0 && (
            <div className="mt-2">
              {result.errors.map((e, i) => <p key={i} className="text-xs text-amber-700">{e}</p>)}
            </div>
          )}
        </div>
      )}

      {history.data && history.data.length > 0 && (
        <section className="card">
          <h2 className="font-display text-xl text-navy mb-4">Import History</h2>
          <div className="space-y-2">
            {history.data.map((h: { id: string; createdAt: Date | string; action: string; metadata: unknown }) => {
              const meta = h.metadata as { fileName?: string; rows?: number; created?: number; updated?: number } | null;
              const sourceLabel = h.action === 'upload.zenbooker' ? 'Zenbooker' : h.action === 'upload.meta' ? 'Meta' : 'CSV';
              return (
                <div key={h.id} className="text-sm border-b border-gray-50 pb-2">
                  <span className="font-medium">{meta?.fileName ?? sourceLabel}</span>
                  <span className="ml-2 text-xs text-gold">{sourceLabel}</span>
                  <span className="text-gray-400 ml-2 text-xs">{new Date(h.createdAt).toLocaleString('en-GB')}</span>
                  {meta && <span className="text-gray-500 ml-2 text-xs">{meta.rows} rows · {meta.created} new · {meta.updated} updated</span>}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
